import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import handleRequestError from '../../utils/handleRequestError';

const initialState = {
  isLoading: true,
  data: [],
  previousDayList: [],
  previousDayRequestId: '',
  requestId: '',
  filters: {
    startdate: null,
    enddate: null,
    locationid: '',
    userid: '',
    companyid: '',
    productid: '',
  },
  error: '',
};

export const fetchHarvestProductivity = createAsyncThunk(
  'harvestProductivity/fetchHarvestProductivity',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_HOST_API_KEY}/harvests/picker-performance`, {
        params: { ...params },
      });

      return response.data;
    } catch (err) {
      return handleRequestError(err, rejectWithValue);
    }
  }
);

// Same report for the day before the selected period, used for the "vs. previous day" column
export const fetchHarvestProductivityPreviousDay = createAsyncThunk(
  'harvestProductivity/fetchHarvestProductivityPreviousDay',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_HOST_API_KEY}/harvests/picker-performance`, {
        params: { ...params },
      });

      return response.data;
    } catch (err) {
      return handleRequestError(err, rejectWithValue);
    }
  }
);

const harvestProductivitySlice = createSlice({
  name: 'harvestProductivitySlice',
  initialState,
  reducers: {
    setData(state, action) {
      state.data = action.payload;
    },
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters(state) {
      state.filters = {
        ...state.filters,
        locationid: '',
        userid: '',
        companyid: '',
        productid: '',
      };
    },
  },
  extraReducers(builder) {
    // Only the last request counts: a slow older one (e.g. a year) must not overwrite a newer period
    builder.addCase(fetchHarvestProductivity.pending, (state, action) => {
      state.isLoading = true;
      state.requestId = action.meta.requestId;
    });
    builder.addCase(fetchHarvestProductivity.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.requestId) return;
      state.isLoading = false;
      state.error = '';
      state.data = action.payload.data;
    });
    builder.addCase(fetchHarvestProductivity.rejected, (state, action) => {
      if (action.meta.requestId !== state.requestId) return;
      state.error = action.payload;
      state.isLoading = false;
    });
    // On error the first day of the period shows "—" (no snackbar: the main report still loads)
    builder.addCase(fetchHarvestProductivityPreviousDay.pending, (state, action) => {
      state.previousDayList = [];
      state.previousDayRequestId = action.meta.requestId;
    });
    builder.addCase(fetchHarvestProductivityPreviousDay.fulfilled, (state, action) => {
      // An older request answering last must not overwrite the current one
      if (action.meta.requestId !== state.previousDayRequestId) return;
      state.previousDayList = action.payload.data?.list || [];
    });
    builder.addCase(fetchHarvestProductivityPreviousDay.rejected, (state, action) => {
      if (action.meta.requestId !== state.previousDayRequestId) return;
      state.previousDayList = [];
    });
  },
});

export const { setData, setFilters, resetFilters } = harvestProductivitySlice.actions;

export default harvestProductivitySlice.reducer;

export const selectHarvestProductivityFilters = (state) => state.harvestProductivity.filters;
export const selectHarvestProductivityData = (state) => state.harvestProductivity.data;
export const selectHarvestProductivityLoading = (state) => state.harvestProductivity.isLoading;
export const selectHarvestProductivityError = (state) => state.harvestProductivity.error;
export const selectHarvestProductivityPreviousDayList = (state) => state.harvestProductivity.previousDayList;
